import type {
  FreeScoutPreview,
  FreeScoutReference,
  Me,
  StaffRequestDetail,
} from "@hoffman-support/contracts";
import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  canUseFreeScout,
  emptySelection,
  findFreeScoutRequest,
  freeScoutCreate,
  freeScoutLink,
  freeScoutPreview,
  type HandoffSelection,
  handoffError,
  handoffReady,
} from "../freescout-api";
import { FreeScoutSelection, FreeScoutSource } from "./FreeScoutSource";
import { RequestForm } from "./RequestForm";
import { STATUS_LABELS } from "./RequestList";

export function FreeScoutHandoff({ me, reference }: { me: Me; reference: FreeScoutReference }) {
  if (!canUseFreeScout(me)) return <p role="alert">Staff only.</p>;
  return <Handoff me={me} reference={reference} />;
}

function Handoff({ me, reference }: { me: Me; reference: FreeScoutReference }) {
  const { instanceId, mailboxId, conversationId } = reference;
  const [preview, setPreview] = useState<FreeScoutPreview | null>(null);
  const [selection, setSelection] = useState<HandoffSelection>(emptySelection);
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);
  const [action, setAction] = useState<"create" | "link">("create");
  const [saving, setSaving] = useState(false);
  const [requestReference, setRequestReference] = useState("");
  const [request, setRequest] = useState<StaffRequestDetail | null>(null);
  const [finding, setFinding] = useState(false);
  const lookupVersion = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload explicitly re-fetches the same conversation after an operator request.
  useEffect(() => {
    let active = true;
    setPreview(null);
    setNotice("");
    setSelection(emptySelection());
    setRequest(null);
    setFinding(false);
    lookupVersion.current++;
    freeScoutPreview({ instanceId, mailboxId, conversationId })
      .then((value) => {
        if (active) setPreview(value);
      })
      .catch((error) => {
        if (active)
          setNotice(handoffError(error, "The conversation could not be loaded. Please try again."));
      });
    return () => {
      active = false;
    };
  }, [instanceId, mailboxId, conversationId, reload]);

  function choose(value: HandoffSelection) {
    if (selection.requester?.id !== value.requester?.id) {
      lookupVersion.current++;
      setFinding(false);
      setRequest(null);
      setNotice("");
    }
    setSelection(value);
  }

  async function findRequest() {
    const requester = selection.requester;
    if (!requester || finding) return;
    const version = ++lookupVersion.current;
    setFinding(true);
    setRequest(null);
    setNotice("");
    try {
      const found = await findFreeScoutRequest(requestReference, requester.id);
      if (lookupVersion.current === version) setRequest(found);
    } catch (error) {
      if (lookupVersion.current === version)
        setNotice(
          handoffError(error, "The request could not be found. Check the reference and try again."),
        );
    } finally {
      if (lookupVersion.current === version) setFinding(false);
    }
  }

  async function link(event: FormEvent) {
    event.preventDefault();
    if (!preview || !request || !handoffReady(preview, selection) || saving) return;
    setSaving(true);
    setNotice("");
    try {
      await freeScoutLink(me, preview, selection, request);
      window.location.assign(
        `/staff/requests/${encodeURIComponent(request.publicId)}?freescout=linked`,
      );
    } catch (error) {
      setNotice(
        handoffError(
          error,
          "The link could not be saved. Your selections are kept; please try again.",
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <article aria-labelledby="freescout-heading">
      <p>
        <a href="/staff">Back to the queue</a>
      </p>
      <h2 id="freescout-heading">Bring an email request into the portal</h2>
      {!preview && !notice ? <p role="status">Loading the FreeScout conversation…</p> : null}
      {notice ? <p role="alert">{notice}</p> : null}
      <p>
        <button
          type="button"
          disabled={saving || (!preview && !notice)}
          onClick={() => setReload((value) => value + 1)}
        >
          Reload conversation
        </button>
      </p>
      {preview ? (
        <>
          <FreeScoutSource preview={preview} />
          {preview.status.requests.length ? (
            <section aria-label="Existing portal link">
              <h3>Linked portal request</h3>
              {preview.status.requests.map((linked) => (
                <p key={linked.id}>
                  <a href={`/staff/requests/${encodeURIComponent(linked.publicId)}`}>
                    {linked.publicId}
                  </a>{" "}
                  · {STATUS_LABELS[linked.status]}
                </p>
              ))}
              {preview.status.syncState !== "linked" ? (
                <p>An administrator can review and repair the source link on the request.</p>
              ) : null}
            </section>
          ) : preview.status.syncState !== "unlinked" ? (
            <p role="alert">
              This conversation needs a link review before it can be used. Ask an administrator to
              review it.
            </p>
          ) : (
            <>
              <FreeScoutSelection
                key={reload}
                preview={preview}
                value={selection}
                onChange={choose}
                disabled={saving}
              />
              <fieldset disabled={saving} style={{ marginBottom: "1rem" }}>
                <legend>What would you like to do?</legend>
                <label>
                  <input
                    type="radio"
                    name="handoff-action"
                    value="create"
                    checked={action === "create"}
                    onChange={() => setAction("create")}
                  />{" "}
                  Create a request
                </label>{" "}
                <label>
                  <input
                    type="radio"
                    name="handoff-action"
                    value="link"
                    checked={action === "link"}
                    onChange={() => setAction("link")}
                  />{" "}
                  Link an existing request
                </label>
              </fieldset>
              {!handoffReady(preview, selection) ? (
                <p>
                  Choose a customer message, select the verified portal account and record how you
                  verified them to continue.
                </p>
              ) : null}
              <div hidden={action !== "create"}>
                <RequestForm
                  me={me}
                  initialSubject={preview.subject}
                  heading="Create a portal request"
                  submitLabel="Create and link request"
                  submitDisabled={saving || !handoffReady(preview, selection)}
                  submitRequest={async (idempotencyKey, body) => {
                    setSaving(true);
                    try {
                      const created = await freeScoutCreate(
                        me,
                        idempotencyKey,
                        preview,
                        selection,
                        body,
                      );
                      window.location.assign(
                        `/staff/requests/${encodeURIComponent(created.publicId)}?created=1&bound=1&source=email${created.redacted ? "&redacted=1" : ""}`,
                      );
                    } finally {
                      setSaving(false);
                    }
                  }}
                />
              </div>
              <form
                hidden={action !== "link"}
                onSubmit={link}
                aria-labelledby="link-request-heading"
              >
                <h3 id="link-request-heading">Link an existing request</h3>
                <p>The request must belong to the verified portal account selected above.</p>
                <label htmlFor="freescout-request-reference">Request reference *</label>
                <br />
                <input
                  id="freescout-request-reference"
                  value={requestReference}
                  placeholder="HOF-000123"
                  maxLength={30}
                  disabled={saving}
                  onChange={(event) => {
                    lookupVersion.current++;
                    setFinding(false);
                    setRequest(null);
                    setRequestReference(event.target.value);
                  }}
                />{" "}
                <button
                  type="button"
                  onClick={() => void findRequest()}
                  disabled={saving || finding || !selection.requester || !requestReference.trim()}
                >
                  Find request
                </button>
                {finding ? <p role="status">Finding the request…</p> : null}
                {request ? (
                  <p>
                    <strong>
                      {request.publicId}: {request.subject}
                    </strong>
                    <br />
                    Requester: {request.requester.upn} · {STATUS_LABELS[request.status]}
                  </p>
                ) : null}
                <p>
                  <button
                    type="submit"
                    disabled={saving || !request || !handoffReady(preview, selection)}
                  >
                    {saving ? "Saving…" : "Link conversation to request"}
                  </button>
                </p>
              </form>
            </>
          )}
        </>
      ) : null}
    </article>
  );
}
